import { ExportAssetType } from '../../../asset/util/useAddPermissions';
import React, { useMemo, useState } from 'react';
import { StyledCompactTable } from '../../../configuration/util/utils';
import { Divider } from 'antd';
import Button from '../../Button';
import {
  AnyAssetSubType,
  Flex,
  Loading,
  NewAsset,
  useAssetTranslation,
  useCustodianQuery,
} from '@mss/mss-component-library';
import { exportAssetTypeSubTypeKey } from './util';
import { assetSubTypeInformation } from './config';
import CorrectAssetSubType from './CorrectAsset';

type UploadResultProps<AssetType> = {
  assetsFromCsv: AssetType[];
  invalidKeys: string[];
  type: ExportAssetType;
  setAssetsForOnboarding: (asset: AssetType[]) => void;
  goToPreviousStep: () => void;
};

function UploadResults<AssetType extends NewAsset>({
  assetsFromCsv,
  invalidKeys,
  type,
  setAssetsForOnboarding,
  goToPreviousStep,
}: UploadResultProps<AssetType>) {
  const { data: custodians, isInitialLoading } = useCustodianQuery();
  const validCustodians = custodians.map(x => x.options).flat();

  const assetSubType = exportAssetTypeSubTypeKey<AssetType>(type);
  const initialAssets = assetsFromCsv.map((x, i) => ({
    ...assetSubTypeInformation(x[assetSubType.key] as any).skeletonObject,
    ...x,
    tempId: i,
  }));
  const [correctedAssets, setCorrectedAssets] =
    useState<AssetType[]>(initialAssets);

  const deleteAsset = (tempId: number) => {
    setCorrectedAssets(x => x.filter(x => x.tempId !== tempId));
  };

  const updateAssetType = (tempId: number, newSubType: string) => {
    setCorrectedAssets(x =>
      x.map(x =>
        x.tempId === tempId
          ? {
              ...assetSubTypeInformation(newSubType).skeletonObject,
              ...x,
              [assetSubType.key]: newSubType,
            }
          : x
      )
    );
  };

  const assetGroupedBySubType = useMemo(() => {
    return initialAssets.reduce(
      (acc, asset) => {
        if (correctedAssets.findIndex(x => x.tempId === asset.tempId) < 0) {
          return acc;
        }

        const subTypeKey = asset[
          assetSubType.key
        ] as unknown as AnyAssetSubType;
        if (assetSubType.options.includes(subTypeKey)) {
          acc[subTypeKey] = [...(acc[subTypeKey] || []), asset];
        } else {
          acc['unknown'] = [...(acc['unknown'] || []), asset];
        }

        return acc;
      },
      {} as Record<string, AssetType[]>
    );
  }, [initialAssets, correctedAssets, assetSubType.key, assetSubType.options]);

  const { t } = useAssetTranslation();
  if (isInitialLoading) {
    return <Loading />;
  }
  return (
    <div>
      {invalidKeys.length > 0 && (
        <>
          <h4>{t('Issues found with CSV')}</h4>
          {t('The following headers are not valid for this asset type')}:{' '}
          {
            <ul>
              {invalidKeys.map(key => (
                <li key={key}>
                  <pre>{key}</pre>
                </li>
              ))}
            </ul>
          }
          {t(
            'You can go back and fix these issues in your CSV or ignore them and resolve these during the onboarding process.'
          )}
        </>
      )}
      {Object.entries(assetGroupedBySubType)
        .sort(
          ([a], [b]) =>
            +(b === 'unknown') - +(a === 'unknown') || a.localeCompare(b)
        )
        .map(([key, assets]) => {
          const isUnknown = key === 'unknown';
          return (
            <div key={key} className="mt-8">
              {isUnknown ? (
                <CorrectAssetSubType
                  assets={assets as any}
                  type={type}
                  updateAssetType={updateAssetType}
                  deleteAsset={deleteAsset}
                />
              ) : (
                <>
                  <h4>
                    {t(assetSubType.labels[key as AnyAssetSubType] ?? key)}
                  </h4>
                  <StyledCompactTable
                    rowClassName="cursor-default"
                    pagination={assets.length > 10}
                    rowKey="tempId"
                    dataSource={assets}
                    columns={[
                      {
                        dataIndex: 'name',
                        key: 'name',
                        title: t('Name'),
                        render: (value: string) => value || '-',
                      },
                      {
                        dataIndex: 'custodian',
                        key: 'custodian',
                        title: t('Custodian'),
                        render: (custodian: string) => {
                          if (!custodian) return '-';

                          return (
                            validCustodians.find(x => x.value === custodian)
                              ?.label || '-'
                          );
                        },
                      },
                    ]}
                  />
                </>
              )}
            </div>
          );
        })}

      <Divider />
      <Flex h="between">
        <Button onClick={goToPreviousStep}>{t('Previous step')}</Button>
        <Button
          disabled={correctedAssets.some(
            x => !assetSubType.options.includes(x[assetSubType.key] as any)
          )}
          onClick={() => setAssetsForOnboarding(correctedAssets)}
          variant="primary"
        >
          {t('Confirm')}
        </Button>
      </Flex>
    </div>
  );
}

export default UploadResults;
